<template>
  <div
    class="markdown-container"
    :class="{ 'rich-dark': dark }"
    :style="{ fontSize: fontSize + 'px' }"
    @click="onClick"
    @load.capture="scheduleMeasure"
    @error.capture="scheduleMeasure"
  >
    <div v-if="prefix" class="rich-prefix">{{ prefix }}</div>
    <div v-html="rendered.html"></div>
  </div>
</template>
<script>
import { renderMarkdown, safeURL } from '@/plugins/richText'
import 'katex/dist/katex.min.css'
export default {
  name: 'MarkdownWithKatex',
  props: {
    content: { type: String, default: '' },
    prefix: { type: String, default: '' },
    dark: Boolean,
    fontSize: { type: Number, default: 16 }
  },
  data: () => ({ renderId: 0, frame: 0 }),
  computed: {
    rendered() {
      try {
        return renderMarkdown(this.content)
      } catch (_) {
        return {
          html:
            '<p>' +
            String(this.content).replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c])) +
            '</p>',
          mathErrors: 1,
          mathCount: 0
        }
      }
    }
  },
  watch: {
    content() {
      this.renderId++
      this.$nextTick(this.scheduleMeasure)
    },
    fontSize() {
      this.$nextTick(this.scheduleMeasure)
    },
    dark() {
      this.$nextTick(this.scheduleMeasure)
    }
  },
  mounted() {
    if (typeof ResizeObserver !== 'undefined') {
      this.observer = new ResizeObserver(this.scheduleMeasure)
      this.observer.observe(this.$el)
    }
    if (document.fonts)
      document.fonts.ready.then(() => {
        if (!this._isDestroyed) this.scheduleMeasure()
      })
    this.scheduleMeasure()
  },
  beforeDestroy() {
    if (this.observer) this.observer.disconnect()
    cancelAnimationFrame(this.frame)
  },
  methods: {
    scheduleMeasure() {
      if (this.frame || this._isDestroyed) return
      this.frame = requestAnimationFrame(() => {
        this.frame = 0
        if (this._isDestroyed) return
        const root = this.$el,
          rect = root.getBoundingClientRect()
        root.querySelectorAll('.comment-math-inline').forEach((math) => {
          math.classList.remove('comment-math-overflow')
          if (math.scrollWidth > rect.width + 1) math.classList.add('comment-math-overflow')
        })
        this.$emit('resize', {
          id: this.renderId,
          height: Math.ceil(root.getBoundingClientRect().height),
          width: rect.width,
          mathErrors: this.rendered.mathErrors,
          mathCount: this.rendered.mathCount
        })
      })
    },
    onClick(event) {
      const link = event.target.closest('a[href]')
      if (link) {
        event.preventDefault()
        const url = safeURL(link.getAttribute('href'))
        if (url) window.open(url, '_blank', 'noopener,noreferrer')
      }
    }
  }
}
</script>
<style lang="scss">
.markdown-container {
  width: 100%;
  min-width: 0;
  line-height: 1.75;
  overflow-wrap: anywhere;
  color: inherit;
  box-sizing: border-box;
}
.markdown-container p {
  margin: 0.6em 0;
}
.markdown-container p:first-child {
  margin-top: 0;
}
.markdown-container p:last-child {
  margin-bottom: 0;
}
.markdown-container h1,
.markdown-container h2,
.markdown-container h3,
.markdown-container h4 {
  font-size: 1.2em;
  line-height: 1.5;
  margin: 1em 0 0.5em;
  color: inherit;
}
.markdown-container ul,
.markdown-container ol {
  padding-left: 1.5em;
  margin: 0.6em 0;
}
.markdown-container ul {
  list-style: disc;
}
.markdown-container ol {
  list-style: decimal;
}
.markdown-container li {
  margin: 0.2em 0;
}
.markdown-container pre {
  white-space: pre;
  overflow-x: auto;
  background: var(--study-soft, #f5f6f8);
  padding: 14px;
  border-radius: 8px;
  max-width: 100%;
  font-size: 0.9em;
}
.markdown-container code {
  font-family: ui-monospace, monospace;
  background: var(--study-soft, #f5f6f8);
}
.markdown-container blockquote {
  border-left: 3px solid #ff4036;
  padding: 8px 16px;
  margin: 12px 0;
  background: var(--study-soft, #f7f8fa);
}
.markdown-container img {
  max-width: 100%;
  height: auto;
}
.markdown-container a {
  color: #dc443e;
  text-decoration: underline;
}
.markdown-container .comment-table-scroll {
  overflow-x: auto;
}
.markdown-container table {
  border-collapse: collapse;
  font-size: 0.93em;
  width: 100%;
}
.markdown-container th,
.markdown-container td {
  border: 1px solid var(--study-line, #e5e7eb);
  padding: 8px 12px;
  min-width: 60px;
}
.markdown-container .comment-math-display,
.markdown-container .comment-math-overflow {
  display: block;
  max-width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  padding: 4px 0;
}
.markdown-container .comment-math-display .katex-display {
  margin: 0.7em 0;
}
.markdown-container .katex {
  font-size: 1.12em;
}
.markdown-container .comment-math-error {
  color: #bd4b45;
  white-space: pre-wrap;
}
.rich-prefix {
  color: #89909b;
  margin-bottom: 6px;
}
.rich-dark {
  color: #e4e7ed;
  --study-soft: #252932;
  --study-line: #414650;
}
@media (prefers-color-scheme: dark) {
  .markdown-container {
    --study-soft: #252932;
    --study-line: #414650;
  }
}
</style>
